// The single typed axios instance + interceptors (doc 04 §5.1).
import axios, { AxiosError, type AxiosInstance } from 'axios'
import type { ApiError } from '@/types/api'
import { useAuthStore } from '@/stores/auth.store'
import { useUiStore } from '@/stores/ui.store'

export const http: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api/v1',
  withCredentials: true, // Sanctum SPA cookie support
  headers: { Accept: 'application/json' },
  timeout: 20_000,
})

// --- Request interceptor: attach Bearer token + locale ---
http.interceptors.request.use((config) => {
  const auth = useAuthStore()
  if (auth.token) {
    config.headers.Authorization = `Bearer ${auth.token}`
  }
  config.headers['Accept-Language'] = useUiStore().locale
  return config
})

// Shape of the error body the Laravel API returns (doc 12 §1.4).
interface ErrorBody {
  message?: string
  error?: { code?: string; details?: Record<string, string[]> }
  errors?: Record<string, string[]>
}

// --- Response interceptor: centralized error handling + envelope passthrough ---
// On success we return the response untouched; per-domain modules read `r.data`
// (the `{ success, message, data, meta }` envelope) and unwrap `.data` themselves.
http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ErrorBody>) => {
    const ui = useUiStore()
    const auth = useAuthStore()
    const status = error.response?.status ?? 0
    const body = error.response?.data

    const normalized: ApiError = {
      status,
      code: body?.error?.code,
      message: body?.message ?? 'Network error. Please try again.',
      errors: body?.error?.details ?? body?.errors,
    }

    switch (status) {
      case 401: // token expired/invalid → force re-auth
        auth.clearSession()
        // Lazy import avoids a circular dependency at module-eval time.
        {
          const { default: router } = await import('@/router')
          if (router.currentRoute.value.name !== 'login') {
            void router.push({
              name: 'login',
              query: { redirect: router.currentRoute.value.fullPath },
            })
          }
        }
        break
      case 403: {
        const { default: router } = await import('@/router')
        void router.push({ name: 'forbidden' })
        break
      }
      case 419: // CSRF token mismatch (stateful SPA) — caller may retry after re-priming
        break
      case 422: // validation: surfaced to the form, not a global toast
        break
      case 429:
        ui.toast('Too many requests. Please slow down.', 'warning')
        break
      default:
        if (status >= 500) {
          ui.toast('Something went wrong on our end.', 'error')
        }
    }

    return Promise.reject(normalized)
  },
)
